import type { FruitSliceFrame, FruitSliceMeta } from '@fruitful/types';
import { usePathname } from 'expo-router';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import {
    BodyMediumEmphasized,
    FootnoteRegular,
    H6Emphasized,
    IconButton,
    LayoutGridIcon,
    PanelRightIcon,
    SegmentedControl,
    Shadows,
    SmoothieScrollView,
    Spinner,
    Text,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { RAIL_WIDTH_CSS_VAR } from '../../common/utils/layout';
import {
    useListFruitSliceFrames,
    useListFruitSlices,
} from '../hooks/useFruitSlices';
import {
    sidebarRailOpenAtom,
    sidebarSliceIdAtom,
    useSidebarRailWidth,
} from '../sidebarAtoms';
import { FruitSliceHost } from './FruitSliceHost';

const TOGGLE_INSET = 24;
const RESIZE_HANDLE_WIDTH = 6;
const KEYBOARD_RESIZE_STEP = 24;

const RAIL_Z_INDEX = 0;
const TOGGLE_Z_INDEX = 1000;
const RESIZE_OVERLAY_Z_INDEX = TOGGLE_Z_INDEX + 1;

/**
 * Classic (`/money-map/...`) and Money Mapper (`/money-mapper/...`) editors.
 * Preview routes (`.../preview/...`) keep the sidebar toggle.
 */
const isMoneyMapEditorPath = (pathname: string) => {
    if (pathname === '/money-mapper' || pathname.startsWith('/money-mapper/')) {
        return true;
    }

    if (pathname === '/money-map' || pathname.startsWith('/money-map/')) {
        return !pathname.includes('/preview/');
    }

    return false;
};

/**
 * App-wide right rail that hosts a slice's SIDEBAR frame alongside whatever
 * Grove page is open. Mounted once by the app stack, not per route.
 */
export const FruitSliceSidebarRail = () => {
    const pathname = usePathname();
    const [isOpen, setIsOpen] = useAtom(sidebarRailOpenAtom);
    const [sliceId, setSliceId] = useAtom(sidebarSliceIdAtom);
    const { width, setWidth } = useSidebarRailWidth();
    const [isResizing, setIsResizing] = useState(false);
    const { data: slices = [] } = useListFruitSlices();

    const hideOnRoute = isMoneyMapEditorPath(pathname);
    const sidebarSlices = slices.filter((slice) => slice.sidebarFrameCount > 0);
    const reservedWidth =
        isOpen && sidebarSlices.length > 0 && !hideOnRoute ? width : 0;

    useEffect(() => {
        document.documentElement.style.setProperty(
            RAIL_WIDTH_CSS_VAR,
            `${reservedWidth}px`,
        );

        // Cleared on unmount so signed-out screens get the full viewport back.
        return () => {
            document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
        };
    }, [reservedWidth]);

    if (sidebarSlices.length === 0 || hideOnRoute) {
        return null;
    }

    const selectedSlice = sidebarSlices.find((slice) => slice.id === sliceId);

    const startResize = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        setIsResizing(true);

        const handleMove = (moveEvent: MouseEvent) => {
            setWidth(window.innerWidth - moveEvent.clientX);
        };

        const handleUp = () => {
            setIsResizing(false);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    };

    const nudgeWidth = (event: { key: string; preventDefault: () => void }) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
        }

        event.preventDefault();
        setWidth(
            width +
                (event.key === 'ArrowLeft'
                    ? KEYBOARD_RESIZE_STEP
                    : -KEYBOARD_RESIZE_STEP),
        );
    };

    if (!isOpen) {
        return (
            <View
                position="fixed"
                bottom={TOGGLE_INSET}
                right={TOGGLE_INSET}
                zIndex={TOGGLE_Z_INDEX}
                borderRadius={16}
                {...Shadows.MEDIUM}
            >
                <IconButton
                    variant="primary"
                    icon={PanelRightIcon}
                    onPress={() => {
                        setIsOpen(true);
                    }}
                />
            </View>
        );
    }

    return (
        <>
            <View
                position="fixed"
                top={0}
                right={0}
                bottom={0}
                width={width}
                zIndex={RAIL_Z_INDEX}
                backgroundColor="$white"
                borderLeftWidth={1}
                borderLeftColor="$gray400"
                {...Shadows.MEDIUM}
            >
                {/* Raw DOM because Grove is web only and this needs the mouse
                    drag lifecycle rather than React Native's touch responder. */}
                <button
                    type="button"
                    aria-label="Resize sidebar"
                    onMouseDown={startResize}
                    onKeyDown={nudgeWidth}
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: RESIZE_HANDLE_WIDTH,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'col-resize',
                        zIndex: 1,
                    }}
                />

                <YStack flex={1} paddingLeft={RESIZE_HANDLE_WIDTH}>
                    <XStack
                        alignItems="center"
                        gap="$2"
                        paddingVertical={12}
                        paddingHorizontal={16}
                        borderBottomWidth={1}
                        borderBottomColor="$gray400"
                    >
                        {selectedSlice != null ? (
                            <>
                                <IconButton
                                    variant="tertiary"
                                    size="tiny"
                                    icon={LayoutGridIcon}
                                    onPress={() => {
                                        setSliceId(null);
                                    }}
                                />
                                <Text fontSize={20}>
                                    {selectedSlice.icon ?? '🍋'}
                                </Text>
                                <BodyMediumEmphasized
                                    flex={1}
                                    numberOfLines={1}
                                >
                                    {selectedSlice.name}
                                </BodyMediumEmphasized>
                            </>
                        ) : (
                            <H6Emphasized flex={1}>Sidebar slices</H6Emphasized>
                        )}
                        <IconButton
                            variant="tertiary"
                            size="tiny"
                            icon={XIcon}
                            onPress={() => {
                                setIsOpen(false);
                            }}
                        />
                    </XStack>

                    {selectedSlice != null ? (
                        <SidebarFrameHost slice={selectedSlice} />
                    ) : (
                        <SidebarSlicePicker
                            slices={sidebarSlices}
                            onSelect={setSliceId}
                        />
                    )}
                </YStack>
            </View>

            {/* The slice iframe swallows mousemove, so drags are tracked on an
                overlay that covers it. */}
            {isResizing && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: RESIZE_OVERLAY_Z_INDEX,
                        cursor: 'col-resize',
                    }}
                />
            )}
        </>
    );
};

const SidebarSlicePicker = ({
    slices,
    onSelect,
}: {
    slices: FruitSliceMeta[];
    onSelect: (sliceId: string) => void;
}) => {
    return (
        <SmoothieScrollView
            flex={1}
            containerStyle={{ width: '100%', padding: 16 }}
        >
            <XStack flexWrap="wrap" gap={12}>
                {slices.map((slice) => (
                    <YStack
                        key={slice.id}
                        width={104}
                        height={104}
                        alignItems="center"
                        justifyContent="center"
                        gap={6}
                        padding={8}
                        borderRadius={16}
                        backgroundColor="$gray200"
                        cursor="pointer"
                        hoverStyle={{ backgroundColor: '$gray300' }}
                        pressStyle={{ backgroundColor: '$gray300' }}
                        onPress={() => {
                            onSelect(slice.id);
                        }}
                    >
                        <Text fontSize={32}>{slice.icon ?? '🍋'}</Text>
                        <FootnoteRegular numberOfLines={2} textAlign="center">
                            {slice.name}
                        </FootnoteRegular>
                    </YStack>
                ))}
            </XStack>
        </SmoothieScrollView>
    );
};

/**
 * Frames have no name, so a version and the tail of the id are all there is to
 * tell two sidebars of one slice apart. Both are needed: versions collide, ids
 * do not.
 */
const frameSwitcherLabel = (frame: FruitSliceFrame) =>
    `v${frame.version} · ${frame.id.slice(-4)}`;

const SidebarFrameHost = ({ slice }: { slice: FruitSliceMeta }) => {
    const { data: frames = [], isPending } = useListFruitSliceFrames(slice.id);
    const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);

    const sidebarFrames = frames.filter(({ type }) => type === 'SIDEBAR');
    // Falling back to the first rather than clearing the selection means a
    // switch to another slice lands on that slice's oldest sidebar by itself.
    const frame =
        sidebarFrames.find(({ id }) => id === selectedFrameId) ??
        sidebarFrames[0];

    if (isPending) {
        return (
            <YStack flex={1} alignItems="center" justifyContent="center">
                <Spinner />
            </YStack>
        );
    }

    if (frame == null) {
        return (
            <YStack flex={1} padding={16}>
                <FootnoteRegular color="$gray1000">
                    {slice.name} no longer has a sidebar frame.
                </FootnoteRegular>
            </YStack>
        );
    }

    return (
        <YStack flex={1} overflow="hidden">
            {sidebarFrames.length > 1 && (
                <XStack
                    paddingHorizontal={16}
                    paddingVertical={8}
                    borderBottomWidth={1}
                    borderBottomColor="$gray400"
                >
                    <SegmentedControl
                        size="small"
                        selected={frame.id}
                        defaultValue={frame.id}
                        onValueChange={setSelectedFrameId}
                        options={sidebarFrames.map((candidate) => ({
                            value: candidate.id,
                            label: frameSwitcherLabel(candidate),
                        }))}
                    />
                </XStack>
            )}
            <FruitSliceHost key={frame.id} slice={slice} frame={frame} />
        </YStack>
    );
};
